import React, { useState } from "react";
import { Box, Typography, TextField, Button, Switch, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { update_school_api } from "../../DAL/FlightSchools/FlightSchools";

/**
 * How long a student's access to each course lasts — XAir's control, not the
 * school's.
 *
 * WHY IT LIVES HERE AND NOT IN THE SCHOOL'S OWN SETTINGS
 * It is a term of the contract the school signed, not a preference they can
 * move. A school that could extend its own students' access could give away
 * what it is billed for. The update endpoint already refuses `tutorsConfig`
 * from anyone who is not an XAir super admin, so this screen matches an
 * enforcement that already exists rather than inventing one.
 *
 * WHAT IT ACTUALLY DOES
 * The term is stamped onto each enrolment when a student is added, and the
 * enrolment is what grants access. Changing it here affects students added
 * FROM NOW ON — it does not retroactively move anyone's expiry, because
 * silently shortening access somebody already has is not something a form
 * field should be able to do.
 */
const DEFAULT_MONTHS = 12;

export default function CourseTerms({ school, catalogue = [], onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const existing = school?.tutorsConfig?.courseTerms || [];
  const [terms, setTerms] = useState(() => {
    const by = {};
    catalogue.forEach((c) => {
      const row = existing.find((t) => t.license === c.license);
      by[c.license] = row ? { months: row.months, noExpiry: row.months === null } : { months: DEFAULT_MONTHS, noExpiry: false };
    });
    return by;
  });
  const [saving, setSaving] = useState(false);

  const set = (license, patch) => setTerms((t) => ({ ...t, [license]: { ...t[license], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const courseTerms = catalogue.map((c) => ({
        license: c.license,
        months: terms[c.license]?.noExpiry ? null : Number(terms[c.license]?.months) || DEFAULT_MONTHS,
      }));
      const r = await update_school_api(school._id, { tutorsConfig: { courseTerms } });
      if (r.code === 200) { enqueueSnackbar("Course terms saved", { variant: "success" }); onSaved?.(); }
      else enqueueSnackbar(r.message || "Could not save the course terms", { variant: "error" });
    } catch {
      enqueueSnackbar("Could not save the course terms", { variant: "error" });
    } finally { setSaving(false); }
  };

  if (!catalogue.length) {
    return (
      <Typography sx={{ fontSize: 13, color: "#8a8580" }}>
        This school's region has no courses configured, so there is no term to set.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: "#57524c", lineHeight: 1.55, mb: 2, maxWidth: 560 }}>
        How long each student's access lasts once they are enrolled. Applies to students added from
        now on — it does not move the expiry of anyone already enrolled.
      </Typography>

      {catalogue.map((c) => {
        const t = terms[c.license] || {};
        return (
          <Box key={c.license} sx={{
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
            border: "1px solid #E8E4DF", borderRadius: "10px", p: 1.75, mb: 1,
          }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a" }}>{c.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: "#8a8580" }}>{c.license}</Typography>
            </Box>

            <TextField
              size="small" type="number" label="Months" disabled={t.noExpiry}
              value={t.noExpiry ? "" : (t.months ?? DEFAULT_MONTHS)}
              onChange={(e) => set(c.license, { months: e.target.value })}
              inputProps={{ min: 1, max: 120 }}
              sx={{ width: 110 }}
            />

            <Tooltip arrow title="Access never expires. Somebody has to choose this — a course with no term would otherwise hand out permanent access by accident.">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Switch size="small" checked={Boolean(t.noExpiry)}
                  onChange={(e) => set(c.license, { noExpiry: e.target.checked })} />
                <Typography sx={{ fontSize: 12, color: "#57524c" }}>No expiry</Typography>
              </Box>
            </Tooltip>
          </Box>
        );
      })}

      <Button onClick={save} disabled={saving} variant="contained" sx={{
        mt: 1, textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "999px",
        backgroundColor: "#c01e2e", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" },
      }}>
        {saving ? "Saving…" : "Save course terms"}
      </Button>
    </Box>
  );
}
